"use client";

import { useRef } from "react";

// Render-storm probe for the job page and Edit Job form.
//
// React #185 (max update depth) only names the component that finally threw,
// and on the job page that is always a Mantine ScrollArea ref. The driver is
// whatever keeps writing state above it. A nested-update loop never reaches
// passive effects, so this runs in the render body: it counts renders of one
// component instance per second and, once the count passes a threshold,
// records which watched values changed between the last few renders and
// which input had focus. The crash report email includes those records.
//
// It never sets state and never throws. Cost per render is a shallow
// reference walk of the watched object.

const WINDOW_MS = 1000;
const THRESHOLD = 20;
const MAX_EVENTS = 6;
const MAX_DIFFS = 6;
const MAX_PATHS = 12;
const DIFF_DEPTH = 4;
const PER_NAME_COOLDOWN_MS = 2000;

const events: string[] = [];
const lastRecordedAt: Record<string, number> = {};

export const getRenderStormEvents = (): string[] => [...events];

type Watched = Record<string, unknown>;

const isObj = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null;

// Paths whose reference changed between two snapshots, walking into objects
// and arrays up to DIFF_DEPTH so a form change reads as
// values.lineItems[3].specimen.parts rather than just values.
const changedPaths = (
  prev: unknown,
  next: unknown,
  depth: number,
  prefix: string,
  out: string[]
) => {
  if (out.length >= MAX_PATHS) return;
  if (Array.isArray(prev) && Array.isArray(next)) {
    if (prev.length !== next.length) {
      out.push(`${prefix}.length ${prev.length}->${next.length}`);
      return;
    }
    for (let i = 0; i < next.length && out.length < MAX_PATHS; i++) {
      if (prev[i] === next[i]) continue;
      const path = `${prefix}[${i}]`;
      if (depth > 0 && isObj(prev[i]) && isObj(next[i])) {
        changedPaths(prev[i], next[i], depth - 1, path, out);
      } else {
        out.push(path);
      }
    }
    return;
  }
  if (!isObj(prev) || !isObj(next) || Array.isArray(prev) !== Array.isArray(next)) {
    out.push(prefix || "(root)");
    return;
  }
  const keys = new Set([...Object.keys(prev), ...Object.keys(next)]);
  for (const key of keys) {
    if (out.length >= MAX_PATHS) return;
    const a = prev[key];
    const b = next[key];
    if (a === b) continue;
    const path = prefix ? `${prefix}.${key}` : key;
    if (depth > 0 && isObj(a) && isObj(b)) {
      changedPaths(a, b, depth - 1, path, out);
    } else {
      out.push(path);
    }
  }
};

const describeFocus = (): string => {
  try {
    const el = document.activeElement as HTMLElement | null;
    if (!el || el === document.body) return "none";
    const tag = el.tagName.toLowerCase();
    const hint =
      el.getAttribute("aria-label") ||
      el.getAttribute("placeholder") ||
      el.getAttribute("name") ||
      el.id ||
      "";
    let label = "";
    const labelledBy = el.getAttribute("aria-labelledby");
    if (labelledBy) {
      label = document.getElementById(labelledBy)?.textContent?.trim() ?? "";
    } else if (el.id) {
      label =
        document
          .querySelector(`label[for="${CSS.escape(el.id)}"]`)
          ?.textContent?.trim() ?? "";
    }
    return `${tag}${hint ? ` "${hint}"` : ""}${label ? ` label="${label}"` : ""}`.slice(
      0,
      120
    );
  } catch {
    return "unknown";
  }
};

// `enabled` gates the whole probe per tenant; when false nothing runs beyond
// the ref declarations, so every other user is untouched.
export const useRenderStormProbe = (
  name: string,
  watched: Watched,
  enabled: boolean
) => {
  const times = useRef<number[]>([]);
  const prev = useRef<Watched | null>(null);
  const diffs = useRef<string[]>([]);
  const armed = useRef(true);

  if (!enabled || typeof window === "undefined") return;

  try {
    const now = performance.now();
    const t = times.current;
    t.push(now);
    while (t.length && now - t[0] > WINDOW_MS) t.shift();

    if (prev.current) {
      const out: string[] = [];
      changedPaths(prev.current, watched, DIFF_DEPTH, "", out);
      diffs.current.push(out.length ? out.join(", ") : "(no watched change)");
      if (diffs.current.length > MAX_DIFFS) diffs.current.shift();
    }
    prev.current = watched;

    if (t.length < THRESHOLD / 2) armed.current = true;

    if (
      t.length >= THRESHOLD &&
      armed.current &&
      Date.now() - (lastRecordedAt[name] ?? 0) > PER_NAME_COOLDOWN_MS
    ) {
      armed.current = false;
      lastRecordedAt[name] = Date.now();
      const line =
        `${new Date().toISOString()} ${name}: ${t.length} renders in ` +
        `${Math.round(now - t[0])}ms; focus=${describeFocus()}; ` +
        `last changes: ${diffs.current.map((d, i) => `#${i + 1} ${d}`).join(" | ")}`;
      events.push(line.slice(0, 1500));
      if (events.length > MAX_EVENTS) events.shift();
      if (process.env.NODE_ENV !== "production") {
        console.warn("[render-storm]", line);
      }
    }
  } catch {
    // Telemetry must never break the app
  }
};
